import { useSyncExternalStore } from 'react';
import type { MeetingNotesCardData } from './meeting-notes-card';

/**
 * Which open conversations are MEETINGS — the write-up card's marker, keyed by conversation id.
 *
 * The chat header wants to say "Meeting · Sat, Sep 13 · 7:35–8:23 PM" over a meeting's conversation,
 * but the header (`FloatingChat`) and the messages (`ChatSurface` → `useChat`) live in different
 * subtrees, and the conversation row the header already reads (`Conversation`) carries no such fact.
 * Threading the messages up through props would couple the header to the panel for one badge, so
 * the panel PUBLISHES here when it has loaded a conversation and the header reads it — the same
 * module-level-store shape as `lib/doc-viewer-presenter.ts` and `lib/modal-escape.ts`.
 *
 * A conversation is a meeting when any row in it carries `meetingNotesCard`; the latest such row
 * wins. Absent means "not a meeting, or not loaded yet", and the header draws its ordinary title.
 */
const byConversation = new Map<string, MeetingNotesCardData>();
const listeners = new Set<() => void>();
let version = 0;

const emit = (): void => {
  version++;
  for (const listener of [...listeners]) listener();
};

/** What the messages panel calls once a conversation's rows are in hand. Idempotent: same value, no emit. */
export function publishMeetingConversation(conversationId: string, card: MeetingNotesCardData | null): void {
  const current = byConversation.get(conversationId) ?? null;
  if (current === card || (current && card && current.meetingSessionId === card.meetingSessionId && current.startedAt === card.startedAt && current.endedAt === card.endedAt)) {
    return;
  }
  if (card) byConversation.set(conversationId, card);
  else byConversation.delete(conversationId);
  emit();
}

/** The latest row carrying the marker, or null. Pure, so the panel's effect stays a one-liner. */
export function latestMeetingCard<M extends { meetingNotesCard?: MeetingNotesCardData }>(messages: readonly M[]): MeetingNotesCardData | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const card = messages[i].meetingNotesCard;
    if (card) return card;
  }
  return null;
}

/**
 * The marker's facts, from a history row instead — the header's fallback when the write-up row is
 * outside the loaded window (see `lookupMeetingByConversation`). Same shape, so the header cannot tell.
 */
export function meetingCardFromHistory(row: {
  meetingSessionId: string;
  startedAt?: number;
  endedAt?: number;
  meetingUrl?: string;
  participants?: { name: string; isHost: boolean }[];
  speakers?: { name: string; isAgent: boolean }[];
}): MeetingNotesCardData {
  return {
    meetingSessionId: row.meetingSessionId,
    ...(row.startedAt !== undefined ? { startedAt: row.startedAt } : {}),
    ...(row.endedAt !== undefined ? { endedAt: row.endedAt } : {}),
    ...(row.meetingUrl ? { meetingUrl: row.meetingUrl } : {}),
    participants: row.participants ?? [],
    speakers: row.speakers ?? [],
  };
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const snapshot = (): number => version;
const serverSnapshot = (): number => 0;

/** The meeting marker of `conversationId`, or null. Re-renders the caller when the store changes. */
export function useMeetingConversation(conversationId: string | null | undefined): MeetingNotesCardData | null {
  useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  return conversationId ? byConversation.get(conversationId) ?? null : null;
}

/** Test seam. */
export function resetMeetingConversationsForTest(): void {
  byConversation.clear();
  listeners.clear();
  version = 0;
}
